/**
 * MXlab — Step indicator of the mx-riders mobile carousel.
 *
 * One button per card, in the same order. The active button follows the card nearest the
 * start of the track while swiping (scroll on the track, one read per animation frame);
 * a click scrolls the track itself, never the page, to its card. The indicator is hidden
 * by CSS from 750px, where every card is visible.
 */

class MxRidersSteps extends HTMLElement {
  connectedCallback() {
    this.track = this.closest('.mx-riders')?.querySelector('.mx-riders__track');
    if (!this.track) return;
    this.cards = [...this.track.children];
    this.buttons = [...this.querySelectorAll('.mx-riders__step')];
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.frame = 0;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onClick = (event) => {
      const index = this.buttons.indexOf(event.target.closest('.mx-riders__step'));
      if (index < 0) return;
      this.track.scrollTo({
        left: this.offset(index),
        behavior: this.reduced ? 'auto' : 'smooth',
      });
    };
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('click', this.onClick);
    cancelAnimationFrame(this.frame);
  }

  /** Scroll position that brings a card to the start of the track. */
  offset(index) {
    return this.cards[index].offsetLeft - this.cards[0].offsetLeft;
  }

  /** The last card cannot reach the start, but at full scroll it is the nearest one. */
  update() {
    const left = this.track.scrollLeft;
    let active = 0;
    this.cards.forEach((_, i) => {
      if (Math.abs(this.offset(i) - left) < Math.abs(this.offset(active) - left)) active = i;
    });
    this.buttons.forEach((button, i) => {
      if (i === active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('mx-riders-steps')) {
  customElements.define('mx-riders-steps', MxRidersSteps);
}
